import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Certification, ValidationError } from '@queries/generated';
import { CertificationsService } from '@services/certifications.service';
import { CurrentUserService } from '@services/current-user.service';
import { EMAIL } from '@util/regex';
import { MessageService } from 'primeng/api';
import { map, switchMap } from 'rxjs/operators';

interface UserUpdateAttributes {
  firstName?: string;
  lastName?: string;
  email?: string;
}

@Component({
  selector: 'vnm-user-update',
  templateUrl: './user-update.component.html',
  styleUrls: ['./user-update.component.scss']
})
export class UserUpdateComponent implements OnInit {
  @Input() certification: Certification;
  @Input() updateAttrs!: UserUpdateAttributes;

  user$ = this.currentUserService.user$;
  errors: ValidationError[];
  updateForm = this.fb.group({
    firstName: [{ value: null, disabled: true }, Validators.required],
    lastName: [{ value: null, disabled: true }, Validators.required],
    email: [{ value: null, disabled: true }, [Validators.required, Validators.pattern(EMAIL)]]
  });
  formState = 'certification';

  constructor(
    private currentUserService: CurrentUserService,
    private certificationsService: CertificationsService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit() {
    const updateKeys = Object.keys(this.updateAttrs);

    if (updateKeys.length) {
      updateKeys.forEach((key) => this.updateForm.get(key).enable());
      this.formState = 'user';
    }
  }

  onUserUpdate() {
    this.errors = [];

    const patch = this.updateForm.value;

    this.updateAccount(patch);
  }

  onCertificationUpdate() {
    this.errors = [];
    // update user
    const { firstName, lastName } = this.updateAttrs;
    this.updateAccount({ firstName, lastName });

    // certificate change request
    this.certificateChangeRequest();
  }

  private certificateChangeRequest() {
    const { firstName, lastName } = this.updateAttrs;
    const { gid: certificationId, issuerName } = this.certification;
    const input = {
      certificationId,
      attributes: {
        issuerName,
        reason: this.translateService.instant('verified-person-update.change-request-reason'),
        userFirstName: firstName,
        userLastName: lastName
      }
    };

    this.certificationsService.createChangeRequest(input).subscribe(
      (changeRequest) => {
        if (changeRequest) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('verified-person-update.change-request-success')
          });
        }
      },
      (error) => (this.errors = [error, ...this.errors])
    );
  }

  private updateAccount(patch: UserUpdateAttributes) {
    this.user$
      .pipe(
        map(({ id }) => id),
        switchMap((id) => this.currentUserService.updateUser({ id, patch }))
      )
      .subscribe(
        (_success) => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('verified-person-update.update-account-success')
          });
        },
        (error) => (this.errors = [error, ...this.errors])
      );
  }
}
